import { CdkDrag } from "@angular/cdk/drag-drop";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTable } from "@angular/material/table";
import { Router } from "@angular/router";
import {
  IPrescriptionInCareplan,
  IPrescriptionInfosResponse,
  PRESCRIPTION_STATUS,
  PRESCRIPTION_TYPE,
  WidgetName,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, Subject, Subscription } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { IWidgetComponent } from "src/app/models/widget.interface";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PatientService } from "src/app/providers/patient.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { PrescriptionService } from "src/app/providers/prescription.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { CareplanChoiceDialogComponent } from "./careplan-choice-dialog/careplan-choice-dialog.component";
import { PendingPrescriptionsDataSource } from "./pending-prescriptions-widget-datasource";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";

@Component({
  selector: "app-pending-prescriptions-widget",
  templateUrl: "./pending-prescriptions-widget.component.html",
  styleUrls: ["./pending-prescriptions-widget.component.scss"],
})
export class PendingPrescriptionsWidgetComponent implements IWidgetComponent, OnInit, OnDestroy {
  @ViewChild(CdkDrag, { static: true }) cdkDrag: CdkDrag;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<IPrescriptionInfosResponse>;

  public isLoading = true;
  public isDraggable = true;
  public displayedColumns: string[] = ["patient", "status", "category", "dateRequest", "actions"];
  public dataSource: PendingPrescriptionsDataSource;
  public pendingPrescriptions: IPrescriptionInfosResponse[] = [];
  private onDestroy$ = new Subject<void>();
  public widgetName = WidgetName;
  prescriptionInfoSubscription: Subscription;
  currentPageSize = 5;
  pendingPrescriptionsCount: number;
  private healthcareServicesRefs: string[];
  currentSubscription: Subscription;

  constructor(
    private prescriptionService: PrescriptionService,
    private preferenceService: PreferenceService,
    private responsiveDialog: ResponsiveDialogService,
    private router: Router,
    private sessionService: SessionService,
    private healthcareService: HealthcareserviceService,
    public responsiveService: ResponsiveService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private patientService: PatientService
  ) {}

  ngOnInit(): void {
    this.dataSource = new PendingPrescriptionsDataSource(this.prescriptionService);

    // Subscribe to services setup changes
    this.sessionService.servicesSetupWatch.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.updateHealthcareServicesRefsAndLoad();
    });

    // If services are already available at init time, load data immediately
    if (this.sessionService.currentService) {
      this.updateHealthcareServicesRefsAndLoad();
    }
  }

  private updateHealthcareServicesRefsAndLoad(): void {
    if (this.sessionService.currentService.reference === "all") {
      // Use all available service references
      this.healthcareServicesRefs = this.healthcareService.availableServices().map((s) => s.asReference.reference);
    } else {
      // Use only the current service reference
      this.healthcareServicesRefs = [this.sessionService.currentService?.reference];
    }

    // Load pending prescriptions
    this.loadPendingPrescriptions();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private loadPendingPrescriptions() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }

    this.currentSubscription = this.prescriptionService
      .getPendingPrescriptionCount({
        services: this.healthcareServicesRefs,
        status: [PRESCRIPTION_STATUS.REQUEST],
      })
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((result) => {
        this.pendingPrescriptionsCount = result?.count;
        this.dataSource.loadData({
          pageNumber: 0,
          pageSize: this.currentPageSize,
          filters: [],
          services: this.healthcareServicesRefs,
          status: [PRESCRIPTION_STATUS.REQUEST],
        });

        this.isLoading = false;
      });
  }

  public validatePrescription(prescription: IPrescriptionInfosResponse): void {
    // For every prescriptions that is not "Telemonitoring"

    if (prescription.category !== "Telemonitoring") {
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            message: this.translateService.instant("prescription.validate.confirm"),
            type: ConfirmationDialogType.CONFIRM,
          },
          disableClose: true,
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((yes) => {
          if (yes) {
            this.prescriptionService
              .acceptPrescription(prescription.prescriptionId)
              .pipe(first())
              .subscribe(() => {
                this.loadPendingPrescriptions();
              });
          }
        });
    } else {
      // For "Telemonitoring" prescriptions
      const dialog = this.responsiveDialog.open(CareplanChoiceDialogComponent, {
        data: {
          prescription: prescription,
        },
      });

      dialog.afterClosed().subscribe((careplanId) => {
        if (!careplanId) return; // If no careplan is selected, do nothing

        this.prescriptionService
          .acceptPrescription(prescription.prescriptionId, careplanId)
          .pipe(first())
          .subscribe(() => {
            this.loadPendingPrescriptions();
          });
      });
    }
  }

  public encodeToB64(str: string): string {
    return Base64Helper.utf8_to_b64(str);
  }

  public editPrescription(elem: IPrescriptionInfosResponse): void {
    this.isLoading = true;
    const prescriptionConfig$ = this.prescriptionService
      .getPrescriptionConfig(elem.prescriptionId, this.sessionService.userLang)
      .pipe(first());
    const prescription$ = this.prescriptionService.getPrescription(elem.prescriptionId).pipe(first());
    const userPatient$ = this.patientService.getPatientUser(elem.patientCaremateId).pipe(first());
    combineLatest([userPatient$, prescription$, prescriptionConfig$])
      .pipe(first())
      .subscribe(([pu, prescription, prescriptionConfig]) => {
        const careplanPrescription: IPrescriptionInCareplan = {
          prescriptionType: prescription.category.coding[0].code as PRESCRIPTION_TYPE,
          officialCareplan: prescriptionConfig.basedOn.officialCareplan.reference,
          required: true,
        };
        this.isLoading = false;
        this.router.navigate(["patientRegistration", "prescription"], {
          state: {
            directToPrescription: true,
            patientUser: pu,
            careplanPrescriptions: [careplanPrescription],
            prescription: prescription,
            careplanRef: prescriptionConfig.basedOn.careplan,
            prescriptionConfig: prescriptionConfig,
            isPendingPrescriptionEdit: true,
          },
        });
      });
  }
}
